import { Component, DestroyRef, afterNextRender, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { UserHttpService } from '../../core/services/user.http.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { environment } from '../../../environments/environment';
// import { jwtDecode } from 'jwt-decode';
import { ToastService } from '../../core/services/toast.service';
import { from, switchMap } from 'rxjs';
import { RecaptchaService } from '../../core/services/recaptcha.service';

declare global {
  interface Window {
    google?: any;
  }
}

@Component({
  selector: 'app-login',
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
  ],
  templateUrl: './login.html',
  styleUrl: './login.scss',
})
export class Login {
  loginForm: FormGroup;
  twoFaForm: FormGroup;
  recoveryForm: FormGroup;
  readonly errorMessage = signal<string>('');
  readonly pendingTwoFa = signal<boolean>(false);
  readonly pendingTwoFaUserId = signal<string>('');
  readonly backupEmailAvailable = signal<boolean>(false);
  readonly maskedBackupEmail = signal<string>('');
  readonly recoveryCodeSent = signal<boolean>(false);

  constructor(
    private fb: FormBuilder,
    private httpService: UserHttpService,
    private router: Router,
    private destroyRef: DestroyRef,
    private toast: ToastService,
    private recaptcha: RecaptchaService,
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });

    this.twoFaForm = this.fb.group({
      token: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
    });

    this.recoveryForm = this.fb.group({
      otp: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
    });

    afterNextRender(() => {
      this.initializeGoogle();
    });
  }

  onSubmit(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    // Start a fresh auth flow so stale sessions cannot bypass 2FA.
    this.resetAuthFlowState();

    this.errorMessage.set('');
    from(this.recaptcha.execute('login')).pipe(
      switchMap((token) => this.httpService.login(this.loginForm.value, token)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: (res) => this.handleAuthSuccess(res),
      error: (error) => this.handleAuthError(error, 'Invalid credentials or insufficient permissions.'),
    });
  }

  onVerify2FALogin(): void {
    this.twoFaForm.markAllAsTouched();
    if (this.twoFaForm.invalid || !this.pendingTwoFaUserId()) {
      return;
    }

    const token = String(this.twoFaForm.get('token')?.value ?? '').trim();
    this.errorMessage.set('');

    this.httpService
      .verify2FALogin(this.pendingTwoFaUserId(), token)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.handleAuthSuccess(res),
        error: () => this.setError('Invalid 2FA code. Please try again.'),
      });
  }

  reset2FALogin(): void {
    this.resetAuthFlowState(false);
    this.twoFaForm.reset({ token: '' });
    this.recoveryForm.reset({ otp: '' });
    this.errorMessage.set('');
  }

  request2FARecovery(): void {
    if (!this.pendingTwoFaUserId() || !this.backupEmailAvailable()) {
      return;
    }

    this.errorMessage.set('');
    this.httpService
      .request2FARecovery(this.pendingTwoFaUserId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.recoveryCodeSent.set(true);
          this.maskedBackupEmail.set(String(res?.maskedBackupEmail ?? this.maskedBackupEmail()));
          this.toast.info(String(res?.message ?? 'Recovery code sent to backup email.'));
        },
        error: (error) => this.setError(error?.error?.message ?? 'Could not send recovery code.'),
      });
  }

  onVerify2FARecovery(): void {
    this.recoveryForm.markAllAsTouched();

    if (this.recoveryForm.invalid || !this.pendingTwoFaUserId()) {
      return;
    }

    const otp = String(this.recoveryForm.get('otp')?.value ?? '').trim();
    this.errorMessage.set('');

    this.httpService
      .verify2FARecovery(this.pendingTwoFaUserId(), otp)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.handleAuthSuccess(res),
        error: (error) => this.setError(error?.error?.message ?? 'Invalid recovery code. Please try again.'),
      });
  }

  private initializeGoogle(): void {
    const clientId = environment.googleClientId?.trim();
    if (!clientId) {
      this.setError('Google login is not configured. Set googleClientId in environment.');
      return;
    }

    this.loadGoogleScript()
      .then(() => {
        if (!window.google?.accounts?.id) {
          this.setError('Google Sign-In failed to initialize.');
          return;
        }

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response: any) => this.onGoogleCredential(response),
          auto_select: true,
          use_fedcm_for_button: true,
        });

        const googleButton = document.getElementById('buttonDiv');
        const googleButtonWidth = Math.floor(googleButton?.getBoundingClientRect().width || 360);

        window.google.accounts.id.renderButton(
          googleButton,
          {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
            shape: 'pill',
            logo_alignment: 'left',
            width: googleButtonWidth,
          },
        );

        window.google.accounts.id.prompt();
      })
      .catch(() => {
        this.setError('Could not load Google Sign-In script.');
      });
  }

  private onGoogleCredential(response: any): void {
    const credential = response?.credential;
    if (!credential) {
      this.setError('Google credential is missing.');
      return;
    }

    // Start a fresh auth flow so stale sessions cannot bypass 2FA.
    this.resetAuthFlowState();

    this.errorMessage.set('');
    this.httpService
      .oAuthLogin('google', credential)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.handleAuthSuccess(res),
        error: (error) => this.handleAuthError(error, 'Google login failed or insufficient permissions.'),
      });
  }

  private handleAuthSuccess(res: any): void {
    if (res?.require2FA) {
      // Ensure no active access token exists before 2FA is completed.
      this.resetAuthFlowState();
      this.pendingTwoFa.set(true);
      this.pendingTwoFaUserId.set(String(res?.userId ?? ''));
      this.backupEmailAvailable.set(!!res?.backupEmailAvailable);
      this.maskedBackupEmail.set(String(res?.maskedBackupEmail ?? ''));
      this.recoveryCodeSent.set(false);
      this.twoFaForm.reset({ token: '' });
      this.recoveryForm.reset({ otp: '' });
      this.setInfo('Two-factor authentication is enabled. Enter your 6-digit authenticator code.');
      return;
    }

    this.resetAuthFlowState(false);

    const finishLogin = (user: any) => {
      const effectiveRole = String(user?.role ?? '').toLowerCase();
      const isVerified = this.toBoolean(user?.verified) ?? false;

      if (['admin', 'super_admin'].includes(effectiveRole)) {
        this.httpService.setCurrentUser(user);
        this.toast.success(String(res?.message ?? 'Login successful.'));
        this.router.navigate([isVerified ? '/dashboard' : '/verify']);
        return;
      }

      this.httpService.logout();
      this.setError(`Login successful, but role "${effectiveRole || 'unknown'}" is not allowed for dashboard.`);
    };

    if (res?.user) {
      finishLogin(res.user);
      return;
    }

    this.httpService.getCurrentUser().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (user) => finishLogin(user),
      error: () => this.setError('Invalid credentials or insufficient permissions.'),
    });

    // Legacy JWT role decode kept temporarily for cleanup. Admin role now comes from /auth/me.
    // const accessToken = res?.token ?? res?.accessToken;
    // const refreshToken = res?.refreshToken;
    // const tokenPayload = this.decodeToken(accessToken);
    // const effectiveRole = String(res?.role ?? tokenPayload?.role ?? '').toLowerCase();
    // const isVerified = this.toBoolean(res?.verified ?? tokenPayload?.verified) ?? false;
    //
    // if (accessToken && effectiveRole === 'admin') {
    //   this.httpService.setTokens(accessToken, refreshToken);
    //   this.toast.success(String(res?.message ?? 'Login successful.'));
    //   this.router.navigate([isVerified ? '/dashboard' : '/verify']);
    //   return;
    // }
    //
    // if (accessToken) {
    //   this.setError(`Login successful, but role "${effectiveRole || 'unknown'}" is not allowed for dashboard.`);
    //   return;
    // }
    //
    // this.setError('Invalid credentials or insufficient permissions.');
  }

  private handleAuthError(error: any, fallbackMessage: string): void {
    const payload = error?.error ?? {};
    if (payload?.code === 'MIGRATED_PASSWORD_RESET_REQUIRED' || payload?.requiresPasswordReset) {
      const email = String(payload?.email || this.loginForm.get('email')?.value || '').trim();
      const message = String(payload?.message || 'Your account was migrated. Please reset your password to continue.');
      this.errorMessage.set(message);
      this.toast.info(message);
      this.router.navigate(['/forget-password'], {
        queryParams: { email, migrated: '1' },
      });
      return;
    }

    this.setError(payload?.message || fallbackMessage);
  }

  private setError(message: string): void {
    this.errorMessage.set(message);
    this.toast.error(message);
  }

  private setInfo(message: string): void {
    this.errorMessage.set(message);
    this.toast.info(message);
  }

  private resetAuthFlowState(logout = true): void {
    if (logout) {
      this.httpService.setCurrentUser(null);
    }
    this.pendingTwoFa.set(false);
    this.pendingTwoFaUserId.set('');
    this.backupEmailAvailable.set(false);
    this.maskedBackupEmail.set('');
    this.recoveryCodeSent.set(false);
  }

  // Legacy JWT role decode kept temporarily for cleanup. Admin role now comes from /auth/me.
  // private decodeToken(accessToken: string | null | undefined): any | null {
  //   if (!accessToken) {
  //     return null;
  //   }
  //
  //   try {
  //     return jwtDecode(accessToken);
  //   } catch {
  //     return null;
  //   }
  // }

  private toBoolean(value: unknown): boolean | null {
    if (value === true || value === 'true' || value === 1 || value === '1') {
      return true;
    }

    if (value === false || value === 'false' || value === 0 || value === '0') {
      return false;
    }

    return null;
  }

  private loadGoogleScript(): Promise<void> {
    if (window.google?.accounts?.id) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const existingScript = document.getElementById('google-gsi-script') as HTMLScriptElement | null;
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(), { once: true });
        existingScript.addEventListener('error', () => reject(), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.id = 'google-gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject();
      document.head.appendChild(script);
    });
  }
}
